import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useSubscription, useApolloClient } from '@apollo/client/react';
import { gql, ApolloClient, InMemoryCache, HttpLink, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { MeetingProvider } from 'amazon-chime-sdk-component-library-react';
import { VideoCallWrapper } from '../components/video/VideoCallInterface';
import { ON_VIDEO_CONFERENCE_UPDATED } from '../graphql/queries';
import { Button } from '../components/ui/button';

// Type definitions for Apollo queries
interface VideoConference {
  id: string;
  title: string;
  status: string;
  hostId: string;
  isRecordingEnabled: boolean;
}

interface GetVideoConferenceData {
  getVideoConference: VideoConference;
}

// GraphQL query for conference details
const GET_VIDEO_CONFERENCE = gql`
  query GetVideoConference($id: ID!) {
    getVideoConference(id: $id) {
      id
      title
      status
      hostId
      isRecordingEnabled
    }
  }
`;

const END_VIDEO_CONFERENCE = gql`
  mutation EndVideoConference($id: ID!) {
    endVideoConference(id: $id) {
      id
      status
    }
  }
`;

interface ChimeMeetingCredentials {
  meetingId: string;
  mediaRegion: string;
  attendeeId: string;
  joinToken: string;
  externalUserId: string;
  meeting: any;
  attendee: any;
}

export function MobileVideoCall() {
  const { conferenceId } = useParams<{ conferenceId: string }>();
  const [searchParams] = useSearchParams();
  const [credentials, setCredentials] = useState<ChimeMeetingCredentials | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [callEnded, setCallEnded] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [userName, setUserName] = useState('Mobile User');
  const [userId, setUserId] = useState('mobile-user');
  const [authToken, setAuthToken] = useState<string | null>(null);

  // Pre-meeting lobby state - will be set to false if credentials come from mobile app
  const [showLobby, setShowLobby] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const videoPreviewRef = useRef<HTMLVideoElement>(null);

  // Create authenticated Apollo client for mutations when we have an auth token
  const authenticatedClient = useRef<ApolloClient<any> | null>(null);
  
  useEffect(() => {
    if (authToken) {
      const httpLink = new HttpLink({
        uri: import.meta.env.VITE_GRAPHQL_ENDPOINT || 'https://zx2l2cfkgzerzaobtoppnuss3u.appsync-api.us-east-1.amazonaws.com/graphql',
      });
      
      const authLink = setContext((_, { headers }) => ({
        headers: {
          ...headers,
          Authorization: authToken,
        },
      }));
      
      authenticatedClient.current = new ApolloClient({
        link: ApolloLink.from([authLink, httpLink]),
        cache: new InMemoryCache(),
      });
      
      console.log('[MobileVideoCall] Created authenticated Apollo client');
    }
  }, [authToken]);

  // End conference mutation - use default client (will be overridden with authenticated client)
  const [endConference] = useMutation(END_VIDEO_CONFERENCE);

  // Parse credentials from URL hash (more secure than query params)
  useEffect(() => {
    try {
      // Try to get credentials from hash first (base64 encoded JSON)
      const hash = window.location.hash.slice(1);
      
      if (hash) {
        const decoded = JSON.parse(atob(hash));
        
        if (decoded.meetingId && decoded.attendeeId && decoded.joinToken) {
          // Build full credentials object
          const fullCredentials: ChimeMeetingCredentials = {
            meetingId: decoded.meetingId,
            mediaRegion: decoded.mediaRegion || 'us-east-1',
            attendeeId: decoded.attendeeId,
            joinToken: decoded.joinToken,
            externalUserId: decoded.externalUserId || 'mobile-user',
            meeting: decoded.meetingJson ? JSON.parse(decoded.meetingJson) : {
              MeetingId: decoded.meetingId,
              MediaRegion: decoded.mediaRegion || 'us-east-1',
            },
            attendee: decoded.attendeeJson ? JSON.parse(decoded.attendeeJson) : {
              AttendeeId: decoded.attendeeId,
              JoinToken: decoded.joinToken,
              ExternalUserId: decoded.externalUserId || 'mobile-user',
            },
          };
          setCredentials(fullCredentials);
          
          // Extract user info from externalUserId (format: "Name#ID")
          const name = decoded.externalUserId?.includes('#') 
            ? decoded.externalUserId.split('#')[0] 
            : decoded.externalUserId || 'Mobile User';
          const id = decoded.externalUserId?.includes('#')
            ? decoded.externalUserId.split('#')[1]
            : decoded.externalUserId || 'mobile-user';
          setUserName(name);
          setUserId(id);
          
          // Set host status from decoded data
          if (decoded.isHost) {
            setIsHost(true);
          }
          
          // Set auth token from decoded data (passed from mobile app)
          if (decoded.authToken) {
            setAuthToken(decoded.authToken);
            console.log('[MobileVideoCall] Auth token received from mobile app');
          }
          
          // If credentials came from mobile app (isMobileStaff flag), skip the lobby
          // The user already went through PreJoinScreen on the mobile app
          if (decoded.isMobileStaff) {
            setShowLobby(false);
          }
          return;
        }
      }

      // Fallback: try query params
      const meetingId = searchParams.get('meetingId');
      const attendeeId = searchParams.get('attendeeId');
      const joinToken = searchParams.get('joinToken');
      const meetingJson = searchParams.get('meetingJson');
      const attendeeJson = searchParams.get('attendeeJson');

      if (meetingId && attendeeId && joinToken) {
        const fullCredentials: ChimeMeetingCredentials = {
          meetingId,
          mediaRegion: searchParams.get('mediaRegion') || 'us-east-1',
          attendeeId,
          joinToken,
          externalUserId: searchParams.get('externalUserId') || 'mobile-user',
          meeting: meetingJson ? JSON.parse(decodeURIComponent(meetingJson)) : {
            MeetingId: meetingId,
            MediaRegion: searchParams.get('mediaRegion') || 'us-east-1',
          },
          attendee: attendeeJson ? JSON.parse(decodeURIComponent(attendeeJson)) : {
            AttendeeId: attendeeId,
            JoinToken: joinToken,
            ExternalUserId: searchParams.get('externalUserId') || 'mobile-user',
          },
        };
        setCredentials(fullCredentials);
      } else {
        setError('Missing meeting credentials. Please try joining again from the app.');
      }
    } catch (err) {
      console.error('Error parsing credentials:', err);
      setError('Invalid meeting credentials. Please try joining again from the app.');
    }
  }, [searchParams]);

  // Query conference details
  const { data, loading: conferenceLoading } = useQuery<GetVideoConferenceData>(GET_VIDEO_CONFERENCE, {
    variables: { id: conferenceId },
    skip: !conferenceId,
  });

  const conference = data?.getVideoConference;

  // Subscribe to conference updates to detect when host ends the call
  useSubscription(ON_VIDEO_CONFERENCE_UPDATED, {
    variables: { conferenceId },
    skip: !conferenceId || callEnded,
    onData: ({ data: subData }) => {
      const updated = (subData?.data as any)?.onVideoConferenceUpdated;
      if (updated && (updated.status === 'COMPLETED' || updated.status === 'CANCELLED')) {
        // Conference was ended by host
        setCallEnded(true);
        // Notify mobile app
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'CALL_ENDED' }));
        }
      }
    },
  });

  // Check if conference is already ended when data loads
  useEffect(() => {
    if (conference && (conference.status === 'COMPLETED' || conference.status === 'CANCELLED')) {
      setCallEnded(true);
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'CALL_ENDED' }));
      }
    }
  }, [conference]);

  // Initialize media devices for lobby preview
  useEffect(() => {
    if (!showLobby || !credentials) return;

    const initializeDevices = async () => {
      try {
        // Request permissions and get devices
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: true, 
          audio: true 
        });
        setLocalStream(stream);

        // Get device list
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const videoInputs = devices.filter(d => d.kind === 'videoinput');
        
        setAudioDevices(audioInputs);
        setVideoDevices(videoInputs);
        
        if (audioInputs.length > 0) setSelectedAudioDevice(audioInputs[0].deviceId);
        if (videoInputs.length > 0) setSelectedVideoDevice(videoInputs[0].deviceId);

        // Attach stream to video preview
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing media devices:', err);
        // Still allow joining without preview
      }
    };

    initializeDevices();

    return () => {
      // Cleanup stream when leaving lobby
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [showLobby, credentials]);

  // Update video preview when camera is toggled
  useEffect(() => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = isCameraOn;
      });
    }
  }, [isCameraOn, localStream]);

  // Update audio when mic is toggled
  useEffect(() => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = isMicOn;
      });
    }
  }, [isMicOn, localStream]);

  // Handle device change
  const handleDeviceChange = async (type: 'audio' | 'video', deviceId: string) => {
    if (type === 'audio') {
      setSelectedAudioDevice(deviceId);
    } else {
      setSelectedVideoDevice(deviceId);
    }

    // Update stream with new device
    try {
      const constraints: MediaStreamConstraints = {
        audio: type === 'audio' ? { deviceId: { exact: deviceId } } : (selectedAudioDevice ? { deviceId: { exact: selectedAudioDevice } } : true),
        video: type === 'video' ? { deviceId: { exact: deviceId } } : (selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true),
      };

      // Stop old stream
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(newStream);

      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = newStream;
      }
    } catch (err) {
      console.error('Error switching device:', err);
    }
  };

  // Join meeting from lobby
  const handleJoinFromLobby = () => {
    // Stop preview stream before joining
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    setShowLobby(false);
  };

  // Handle cancel - notify mobile app
  const handleCancel = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'CALL_LEFT' }));
    }
  };

  // Handle call end (host ends meeting for everyone)
  const handleCallEnd = async () => {
    try {
      // If host, actually end the conference
      if (isHost && conferenceId) {
        console.log('[MobileVideoCall] Host ending conference:', conferenceId);
        
        // Use authenticated client if available, otherwise fall back to default
        if (authenticatedClient.current) {
          console.log('[MobileVideoCall] Using authenticated client for end conference');
          const result = await authenticatedClient.current.mutate({
            mutation: END_VIDEO_CONFERENCE,
            variables: { id: conferenceId },
          });
          console.log('[MobileVideoCall] End conference result:', result);
          
          if (!result.data?.endVideoConference) {
            console.error('[MobileVideoCall] End conference returned no data');
          }
        } else {
          console.log('[MobileVideoCall] Using default client for end conference (no auth token)');
          const result = await endConference({ variables: { id: conferenceId } });
          console.log('[MobileVideoCall] End conference result:', result);
          
          if (!result.data?.endVideoConference) {
            console.error('[MobileVideoCall] End conference returned no data');
          }
        }
      }
      
      setCallEnded(true);
      // Post message to mobile app
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'CALL_ENDED' }));
      }
    } catch (err: any) {
      console.error('[MobileVideoCall] Error ending conference:', err);
      // Still mark as ended locally even if mutation fails
      // The user has left the call, we just couldn't update the backend
      setCallEnded(true);
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ 
          type: 'CALL_ENDED',
          error: err.message || 'Failed to end conference on server'
        }));
      }
    }
  };

  // Handle leave call (non-host leaves)
  const handleLeaveCall = () => {
    setCallEnded(true);
    // Post message to mobile app
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'CALL_LEFT' }));
    }
  };

  // Loading state - waiting for credentials
  if (!credentials && !error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading video call...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg p-8 max-w-md text-center">
          <div className="bg-white rounded-lg p-1.5 mx-auto mb-4 w-fit">
            <img src="/icon.png" alt="AALaw" className="h-8 w-8" />
          </div>
          <div className="w-16 h-16 bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Unable to Join</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  // Call ended state
  if (callEnded) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg p-8 max-w-md text-center">
          <div className="bg-white rounded-lg p-1.5 mx-auto mb-4 w-fit">
            <img src="/icon.png" alt="AALaw" className="h-8 w-8" />
          </div>
          <div className="w-16 h-16 bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Call Ended</h1>
          <p className="text-gray-400">You can close this window now.</p>
        </div>
      </div>
    );
  }

  // Pre-meeting lobby screen
  if (showLobby && credentials && !conferenceLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        {/* Header */}
        <div className="bg-gray-800 px-4 py-3 border-b border-gray-700 safe-area-top">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1">
              <img src="/icon.png" alt="AALaw" className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white truncate">{conference?.title || 'Video Conference'}</h1>
              <p className="text-gray-400 text-sm">
                {conference?.status === 'SCHEDULED' ? 'Ready to start' : 'Meeting in progress'}
              </p>
            </div>
          </div>
        </div>

        {/* Main content - optimized for mobile */}
        <div className="flex-1 flex flex-col p-4 overflow-auto">
          {/* Video Preview */}
          <div className="relative mb-4">
            <div className="aspect-video bg-gray-800 rounded-xl overflow-hidden relative max-h-[40vh]">
              {isCameraOn ? (
                <video
                  ref={videoPreviewRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover transform scale-x-[-1]"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center">
                    <span className="text-3xl font-semibold text-white">
                      {userName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Camera/Mic toggle overlay */}
              <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center gap-3">
                <button
                  onClick={() => setIsMicOn(!isMicOn)}
                  className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
                    isMicOn ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isMicOn ? (
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => setIsCameraOn(!isCameraOn)}
                  className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
                    isCameraOn ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isCameraOn ? (
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Device status */}
            <div className="mt-3 flex items-center justify-center gap-4 text-sm">
              <span className={`flex items-center gap-1.5 ${isMicOn ? 'text-green-400' : 'text-red-400'}`}>
                <span className={`w-2 h-2 rounded-full ${isMicOn ? 'bg-green-400' : 'bg-red-400'}`}></span>
                {isMicOn ? 'Mic on' : 'Mic off'}
              </span>
              <span className={`flex items-center gap-1.5 ${isCameraOn ? 'text-green-400' : 'text-red-400'}`}>
                <span className={`w-2 h-2 rounded-full ${isCameraOn ? 'bg-green-400' : 'bg-red-400'}`}></span>
                {isCameraOn ? 'Camera on' : 'Camera off'}
              </span>
            </div>
          </div>

          {/* Settings Panel */}
          <div className="bg-gray-800 rounded-xl p-4 flex-1">
            <h2 className="text-base font-semibold text-white mb-4">Ready to join?</h2>
            
            {/* Device selectors */}
            <div className="space-y-3 mb-5">
              {/* Microphone */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Microphone</label>
                <select
                  value={selectedAudioDevice}
                  onChange={(e) => handleDeviceChange('audio', e.target.value)}
                  className="w-full bg-gray-700 text-white rounded-lg px-3 py-2.5 text-sm border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  {audioDevices.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Camera */}
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Camera</label>
                <select
                  value={selectedVideoDevice}
                  onChange={(e) => handleDeviceChange('video', e.target.value)}
                  className="w-full bg-gray-700 text-white rounded-lg px-3 py-2.5 text-sm border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  {videoDevices.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Join button */}
            <Button
              onClick={handleJoinFromLobby}
              className="w-full py-3 text-base font-semibold bg-blue-600 hover:bg-blue-700"
            >
              Join Meeting
            </Button>

            {/* Cancel button */}
            <Button
              variant="ghost"
              onClick={handleCancel}
              className="w-full mt-2 text-gray-400 hover:text-white"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Video call - after joining from lobby
  if (credentials && conferenceId && !showLobby) {
    return (
      <MeetingProvider>
        <VideoCallWrapper
          credentials={credentials}
          conferenceId={conferenceId}
          conferenceTitle={conference?.title || 'Video Conference'}
          isHost={isHost}
          isRecordingEnabled={conference?.isRecordingEnabled || false}
          currentUserId={userId}
          currentUserName={userName}
          currentUserRole="STAFF"
          canEndMeeting={isHost}
          onEndCall={handleCallEnd}
          onLeaveCall={handleLeaveCall}
        />
      </MeetingProvider>
    );
  }

  return null;
}

// Extend Window interface for React Native WebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}
